/**
 * Helpers for the IPO Performance page: listing-day gains and current returns
 * calculated from issue, listing-day and market prices, board tabs, return filters,
 * summary stats, formatting and CSV export.
 */
import { parseIso } from './ipoCalendar';
import { formatClock } from './ipoSubscription';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const PERFORMANCE_TABS = [
  { key: 'mainboard', label: 'Mainboard IPO', noun: 'Mainboard' },
  { key: 'sme', label: 'SME IPO', noun: 'SME' },
];

export const performanceTabByKey = (key) => PERFORMANCE_TABS.find((tab) => tab.key === key) || PERFORMANCE_TABS[0];

const percentChange = (from, to) => ((to - from) / from) * 100;

// Adds listing-day gain and current return (% vs issue price) plus the CMP multiple
export const withReturns = (ipo) => ({
  ...ipo,
  listingGain: percentChange(ipo.issuePrice, ipo.listingClose),
  currentReturn: percentChange(ipo.issuePrice, ipo.cmp),
  multiple: ipo.cmp / ipo.issuePrice,
});

// At least doubled from the issue price
export const isMultibagger = (row) => row.currentReturn >= 100;

// 3.02x -> '3X RETURN'
export const returnBadge = (row) => (isMultibagger(row) ? `${Math.floor(row.multiple)}X RETURN` : null);

export const RETURN_FILTERS = [
  { key: 'all', label: 'All Issues', test: () => true },
  { key: 'multibagger', label: 'Multibaggers (>100%)', test: isMultibagger },
  { key: 'profit', label: 'Positive Profit (>0%)', test: (row) => row.currentReturn > 0 },
  { key: 'discount', label: 'Discount Listings (<0%)', test: (row) => row.listingGain < 0 },
];

export const listingYears = (ipos) =>
  [...new Set(ipos.map((ipo) => Number(ipo.listedOn.slice(0, 4))))].sort((a, b) => b - a);

// One board's listings for a year with returns, newest listing first (same-day listings keep data order)
export const selectListings = (ipos, { tab = 'mainboard', year }) =>
  ipos
    .filter((ipo) => ipo.board === tab && ipo.listedOn.startsWith(`${year}-`))
    .map(withReturns)
    .sort((a, b) => b.listedOn.localeCompare(a.listedOn));

export const filterListings = (rows, { filter = 'all', query = '' } = {}) => {
  const test = (RETURN_FILTERS.find((option) => option.key === filter) || RETURN_FILTERS[0]).test;
  const needle = query.trim().toLowerCase();
  return rows
    .filter(test)
    .filter((row) => !needle || `${row.company} ${row.symbol} ${row.sector}`.toLowerCase().includes(needle));
};

const average = (values) => (values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null);

export const computePerformanceStats = (rows) => {
  const gainers = rows.filter((row) => row.listingGain > 0).length;
  return {
    total: rows.length,
    raisedCr: rows.reduce((sum, row) => sum + row.issueSizeCr, 0),
    avgListingGain: average(rows.map((row) => row.listingGain)),
    avgCurrentReturn: average(rows.map((row) => row.currentReturn)),
    top: rows.reduce((best, row) => (!best || row.currentReturn > best.currentReturn ? row : best), null),
    gainers,
    discounts: rows.filter((row) => row.listingGain < 0).length,
    winRatio: rows.length ? (gainers / rows.length) * 100 : null,
  };
};

export const debutMood = (avgListingGain) => {
  if (avgListingGain === null) return 'No listings yet';
  if (avgListingGain >= 20) return 'Strong debut demand';
  if (avgListingGain >= 5) return 'Healthy debut demand';
  return avgListingGain >= 0 ? 'Muted debuts' : 'Weak debuts on average';
};

const roundTo2 = (value) => Number(value.toFixed(2));

// Sign of the value as displayed, so '0.00%' is never shown as a gain or loss
export const returnTone = (value) => {
  const rounded = roundTo2(value);
  if (rounded > 0) return 'gain';
  return rounded < 0 ? 'loss' : 'flat';
};

// 9.8136 -> '+9.81%', -4.4603 -> '−4.46%'
export const formatReturn = (value) => {
  const rounded = roundTo2(value);
  const sign = { gain: '+', loss: '−', flat: '' }[returnTone(value)];
  return `${sign}${Math.abs(rounded).toFixed(2)}%`;
};

// 1081.7 -> '₹1,081.70'
export const formatPrice = (value) =>
  `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// 68490.4 -> '₹68,490 Cr'
export const formatCroresRaised = (value) => `₹${Math.round(value).toLocaleString('en-IN')} Cr`;

// '2026-09-08' -> 'Tue, Sep 8, 2026'
export const formatListingDate = (iso) => {
  const date = parseIso(iso);
  return `${WEEKDAYS[date.getDay()].slice(0, 3)}, ${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

// '2026-09-08T16:00' -> { short: 'Sep 8, 2026, 4:00 PM IST', long: 'Tuesday, Sep 8, 2026 at 4:00 PM IST' }
export const formatAsOf = (asOf) => {
  const [dateIso, time] = asOf.split('T');
  const date = parseIso(dateIso);
  const day = `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  const clock = formatClock(time);
  return { short: `${day}, ${clock} IST`, long: `${WEEKDAYS[date.getDay()]}, ${day} at ${clock} IST` };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildPerformanceCsv = (rows, asOf) => {
  const header = [
    'Company', 'Symbol', 'Board', 'Exchanges', 'Sector', 'Listed On', 'Issue Price (INR)', 'Listing Day Close (INR)',
    'Listing Day Gain (%)', 'Current Price (INR)', 'Current Profit / Loss (%)', 'Issue Size (INR Cr)', 'Price As On (IST)',
  ];
  const lines = rows.map((row) =>
    [
      row.company,
      row.symbol,
      row.board === 'sme' ? 'SME' : 'Mainboard',
      row.exchanges.join(' / '),
      row.sector,
      row.listedOn,
      row.issuePrice.toFixed(2),
      row.listingClose.toFixed(2),
      row.listingGain.toFixed(2),
      row.cmp.toFixed(2),
      row.currentReturn.toFixed(2),
      row.issueSizeCr,
      asOf.replace('T', ' '),
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
